import { Clock } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useTEF } from 'src/tef/components/providers/TEFProvider';
import { cn } from 'src/tef/components/utils/cn';

interface TimePickerProps {
  value: string | null;
  onChange: (time: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  use24Hour?: boolean;
  minuteStep?: number;
  variant?: {
    size?: 'sm' | 'md' | 'lg';
  };
  className?: string;
}

const sizeStyles = {
  sm: 'py-1.5 px-3 text-sm',
  md: 'py-2 px-4 text-base',
  lg: 'py-3 px-5 text-lg',
};

function generateHours(use24Hour: boolean): string[] {
  const hours = [];
  for (let i = use24Hour ? 0 : 1; i <= (use24Hour ? 23 : 12); i++) {
    hours.push(i.toString().padStart(2, '0'));
  }
  return hours;
}

function generateMinutes(step: number): string[] {
  const minutes = [];
  for (let i = 0; i < 60; i += step) {
    minutes.push(i.toString().padStart(2, '0'));
  }
  return minutes;
}

export default function TimePicker({
  value,
  onChange,
  placeholder = 'Select time...',
  disabled = false,
  use24Hour = false,
  minuteStep = 15,
  variant = {},
  className,
}: TimePickerProps) {
  const tef = useTEF();
  const [isOpen, setIsOpen] = useState(false);
  const size = variant.size || 'md';
  const containerRef = useRef<HTMLElement>(null);

  const hours = generateHours(use24Hour);
  const minutes = generateMinutes(minuteStep);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const parseValue = (val: string | null) => {
    if (!val) return { hour: '', minute: '', period: 'AM' };
    const [time, period] = val.split(' ');
    const [hour, minute] = time.split(':');
    return { hour, minute, period: period || 'AM' };
  };

  const { hour, minute, period } = parseValue(value);

  const handleHourChange = (newHour: string) => {
    const newTime = use24Hour ? `${newHour}:${minute || '00'}` : `${newHour}:${minute || '00'} ${period}`;
    onChange(newTime);
  };

  const handleMinuteChange = (newMinute: string) => {
    const newTime = use24Hour ? `${hour || '00'}:${newMinute}` : `${hour || '12'}:${newMinute} ${period}`;
    onChange(newTime);
  };

  const handlePeriodChange = (newPeriod: string) => {
    onChange(`${hour || '12'}:${minute || '00'} ${newPeriod}`);
  };

  return (
    <section ref={containerRef} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={cn(
          'w-full flex items-center justify-between gap-2',
          'bg-white dark:bg-slate-800',
          'border border-slate-200 dark:border-slate-700',
          'rounded-lg',
          'focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary',
          'transition-colors',
          disabled && 'opacity-50 cursor-not-allowed',
          sizeStyles[size]
        )}
      >
        <span className={value ? tef.colors.foreground : tef.colors.foregroundTertiary}>{value || placeholder}</span>
        <Clock className="w-4 h-4 text-slate-400" />
      </button>

      {isOpen && (
        <article
          className={cn(
            'absolute z-[60] mt-2 left-0',
            'bg-white dark:bg-slate-800',
            'border border-slate-200 dark:border-slate-700',
            'rounded-lg shadow-lg',
            'p-3'
          )}
        >
          <section className="flex gap-3">
            <section className="flex flex-col">
              <span className={cn('text-xs mb-1.5 px-1 font-medium', tef.colors.foregroundTertiary)}>Hour</span>
              <menu className="h-44 overflow-auto pr-1">
                {hours.map((h) => (
                  <li key={h}>
                    <button
                      type="button"
                      onClick={() => handleHourChange(h)}
                      className={cn(
                        'w-12 py-1.5 text-center rounded text-sm transition-colors',
                        hour === h ? 'bg-primary text-white' : 'hover:bg-slate-100 dark:hover:bg-slate-700'
                      )}
                    >
                      {h}
                    </button>
                  </li>
                ))}
              </menu>
            </section>

            <section className="flex flex-col">
              <span className={cn('text-xs mb-1.5 px-1 font-medium', tef.colors.foregroundTertiary)}>Min</span>
              <menu className="h-44 overflow-auto pr-1">
                {minutes.map((m) => (
                  <li key={m}>
                    <button
                      type="button"
                      onClick={() => handleMinuteChange(m)}
                      className={cn(
                        'w-12 py-1.5 text-center rounded text-sm transition-colors',
                        minute === m ? 'bg-primary text-white' : 'hover:bg-slate-100 dark:hover:bg-slate-700'
                      )}
                    >
                      {m}
                    </button>
                  </li>
                ))}
              </menu>
            </section>

            {!use24Hour && (
              <section className="flex flex-col">
                <span className={cn('text-xs mb-1.5 px-1 font-medium', tef.colors.foregroundTertiary)}>Period</span>
                <menu>
                  {['AM', 'PM'].map((p) => (
                    <li key={p}>
                      <button
                        type="button"
                        onClick={() => handlePeriodChange(p)}
                        className={cn(
                          'w-12 py-1.5 text-center rounded text-sm transition-colors',
                          period === p ? 'bg-primary text-white' : 'hover:bg-slate-100 dark:hover:bg-slate-700'
                        )}
                      >
                        {p}
                      </button>
                    </li>
                  ))}
                </menu>
              </section>
            )}
          </section>

          <footer className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className={cn('w-full py-1.5', 'bg-primary text-white', 'rounded-lg', tef.typography.textSm)}
            >
              Done
            </button>
          </footer>
        </article>
      )}
    </section>
  );
}

export { TimePicker };
